import {
  ChakraBox,
  ChakraFlex,
  ChakraTooltip,
  FAIconName,
  Icon,
  Text,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { ReactNode } from 'react';
import ProviderAvatarBlockTooltip from '~/components/common/ProviderAvatarBlockTooltip';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';

const DATE_AT_TIME_FORMAT = "MMMM d, yyyy 'at' h:mm a";

type Props = {
  createdByUserId: string | null;
  effectiveDate: string;
  frequencyLabel: string;
  id: string;
  label: string;
  /**
   * Whether the tooltip shows who changed the frequency and when, below the label. Each line
   * still depends on its own data being present.
   */
  showChangeDetails?: boolean;
};

export const CarePlanFrequencyTooltipIcon = ({
  createdByUserId,
  effectiveDate,
  frequencyLabel,
  id,
  label,
  showChangeDetails = true,
}: Props) => {
  const parsedDate = DateTime.fromISO(effectiveDate);
  const formattedDate =
    showChangeDetails && parsedDate.isValid
      ? parsedDate.toFormat(DATE_AT_TIME_FORMAT)
      : '';
  const changeAuthorId = showChangeDetails ? createdByUserId : null;

  const tooltipBody = (
    <ChakraFlex direction="column" gap="8px">
      <Text variant="body">{label}</Text>
      {!!changeAuthorId && (
        <ProviderAvatarBlockTooltip
          providerExternalId={changeAuthorId}
          TOP_LEVEL_ID={`${id}-frequency-tooltip-provider`}
        />
      )}
      {!!formattedDate && (
        <Text color="neutral.secondary" variant="caption">
          {formattedDate}
        </Text>
      )}
    </ChakraFlex>
  );

  const trigger = (
    <ChakraBox
      aria-label={
        showChangeDetails
          ? `${frequencyLabel}, frequency change details`
          : `${frequencyLabel}, ${label}`
      }
      as="button"
      background="transparent"
      border="none"
      cursor="pointer"
      data-testid={`${id}-frequency-tooltip-icon`}
      padding={0}
      type="button"
    >
      <ChakraFlex alignItems="center" gap="6px">
        <Icon
          color="neutral.secondary"
          name={FAIconName.calendarDayRegular}
          size="sm"
        />
        <Text variant="body">{frequencyLabel}</Text>
      </ChakraFlex>
    </ChakraBox>
  );

  return changeAuthorId ? (
    <ProviderAvatarTooltipWrapper
      placement="top"
      providerExternalId={changeAuthorId}
      tooltipLabel={tooltipBody as ReactNode}
    >
      {trigger}
    </ProviderAvatarTooltipWrapper>
  ) : (
    <ChakraTooltip
      bg="white"
      borderRadius="8px"
      color="neutral.primary"
      label={tooltipBody}
      padding="12px"
      placement="top"
      shouldWrapChildren
    >
      {trigger}
    </ChakraTooltip>
  );
};
